import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useRouteContext } from '@tanstack/react-router';
import { useState } from 'react';
import { Button, ErrorState, MetricTooltip } from '@bb/ui';
import { ApiError, type ChannelDashboardView, type DashboardChannel, type DashboardFilters } from '../../api/client.ts';
import { CompareChart } from './CompareChart.tsx';

const CHANNELS: Array<[DashboardChannel, string]> = [
  ['push', 'Push'],
  ['content_card', 'Content Cards'],
  ['in_app', 'In-App Messages'],
];
const COMPARE: Array<[NonNullable<DashboardFilters['compareBy']>, string]> = [
  ['none', 'Nothing'],
  ['category', 'Transactional vs marketing'],
  ['campaign', 'Campaign or Canvas'],
  ['platform', 'Platform'],
];
const DEFAULT: DashboardFilters = { days: 30, compareBy: 'none' };

interface SavedViews {
  views: Array<{ name: string; filters: DashboardFilters }>;
}

const formatValue = (format: string, value: number) => (format === 'percent' ? `${(value * 100).toFixed(1)} %` : value.toLocaleString('en-US'));
const errorText = (error: unknown) => (error instanceof ApiError ? error.message : 'Something went wrong.');

function GroupHeader({ environmentId, group }: { environmentId: string; group: ChannelDashboardView['groups'][number] }) {
  if (group.link?.type === 'campaign')
    return (
      <Link to="/env/$environmentId/campaigns/$campaignId" params={{ environmentId, campaignId: group.link.id }}>
        {group.label}
      </Link>
    );
  if (group.link?.type === 'canvas')
    return (
      <Link to="/env/$environmentId/canvas/$canvasId" params={{ environmentId, canvasId: group.link.id }}>
        {group.label}
      </Link>
    );
  return <>{group.label}</>;
}

/**
 * Ready-made channel dashboards (BB-134): the channel's registry metrics with their formulas, a comparison of groups
 * on one chart (transactional vs marketing, campaigns, platforms) and personal saved views.
 */
export function ChannelDashboardPage() {
  const { environmentId, channel } = useParams({ from: '/env/$environmentId/dashboards/$channel' }) as { environmentId: string; channel: DashboardChannel };
  const { api } = useRouteContext({ from: '/env/$environmentId/dashboards/$channel' });
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<DashboardFilters>(DEFAULT);
  const [chartMetric, setChartMetric] = useState<string | null>(null);
  const [viewName, setViewName] = useState('');
  const set = (patch: Partial<DashboardFilters>) => setFilters((f) => ({ ...f, ...patch }));
  const view = useQuery({ queryKey: ['dashboards', environmentId, channel, filters], queryFn: () => api.channelDashboard(environmentId, channel, filters), placeholderData: keepPreviousData });
  const prefKey = `dashboards.${channel}`;
  const saved = useQuery({ queryKey: ['preferences', prefKey], queryFn: () => api.getPreference<SavedViews>(prefKey) });
  const views = saved.data?.value?.views ?? [];
  const save = useMutation({
    mutationFn: (name: string) => api.putPreference<SavedViews>(prefKey, { views: [...views.filter((v) => v.name !== name), { name, filters }] }),
    onSuccess: () => {
      setViewName('');
      return queryClient.invalidateQueries({ queryKey: ['preferences', prefKey] });
    },
  });
  const data = view.data?.channel === channel ? view.data : undefined;
  const metric = data?.metrics.find((m) => m.key === chartMetric) ?? data?.metrics.find((m) => m.format === 'percent') ?? data?.metrics[0];
  return (
    <section>
      <h1>Channel dashboards</h1>
      <nav aria-label="Channels" className="toolbar">
        {CHANNELS.map(([key, label]) => (
          <Link key={key} to="/env/$environmentId/dashboards/$channel" params={{ environmentId, channel: key }} aria-current={key === channel ? 'page' : undefined} className={key === channel ? 'button button--active' : 'button'}>
            {label}
          </Link>
        ))}
      </nav>
      <div className="toolbar" role="group" aria-label="Filters">
        <label>
          Period{' '}
          <select value={filters.days} onChange={(e) => set({ days: Number(e.target.value) })}>
            {[7, 30, 90].map((d) => (
              <option key={d} value={d}>
                Last {d} days
              </option>
            ))}
          </select>
        </label>
        <label>
          Platform{' '}
          <select value={filters.platform ?? ''} onChange={(e) => set({ platform: (e.target.value || undefined) as DashboardFilters['platform'] })}>
            <option value="">All platforms</option>
            <option value="android">Android</option>
            <option value="ios">iOS</option>
          </select>
        </label>
        <label>
          Category{' '}
          <select value={filters.category ?? ''} onChange={(e) => set({ category: (e.target.value || undefined) as DashboardFilters['category'] })}>
            <option value="">All messages</option>
            <option value="transactional">Transactional</option>
            <option value="marketing">Marketing</option>
          </select>
        </label>
        <label>
          Compare{' '}
          <select value={filters.compareBy ?? 'none'} onChange={(e) => set({ compareBy: e.target.value as DashboardFilters['compareBy'] })}>
            {COMPARE.map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="toolbar" role="group" aria-label="Saved views">
        {views.length > 0 && (
          <label>
            Saved view{' '}
            <select value="" onChange={(e) => e.target.value && setFilters(views.find((v) => v.name === e.target.value)!.filters)}>
              <option value="">Choose…</option>
              {views.map((v) => (
                <option key={v.name} value={v.name}>
                  {v.name}
                </option>
              ))}
            </select>
          </label>
        )}
        <label>
          View name <input value={viewName} onChange={(e) => setViewName(e.target.value)} maxLength={60} />
        </label>
        <Button onClick={() => save.mutate(viewName.trim())} disabled={!viewName.trim() || save.isPending}>
          Save view
        </Button>
        {save.isError && <span role="alert">{errorText(save.error)}</span>}
      </div>
      {view.isError ? (
        <ErrorState title="The dashboard could not be loaded" message={errorText(view.error)} onRetry={() => void view.refetch()} />
      ) : !data ? (
        <p aria-busy="true">Loading dashboard…</p>
      ) : (
        <>
          <p className="hint">
            {data.from} – {data.to}, send days in {data.timezone}; opens and clicks count on the day of their send. Numbers are live.
          </p>
          {data.groups.length === 0 ? (
            <p className="hint">No messages of this channel in the period.</p>
          ) : (
            <>
              <table className="table" aria-label="Metrics">
                <thead>
                  <tr>
                    <th scope="col">Metric</th>
                    {data.groups.map((g) => (
                      <th key={g.key} scope="col">
                        <GroupHeader environmentId={environmentId} group={g} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.metrics.map((m) => (
                    <tr key={m.key}>
                      <th scope="row">{m.title.en}</th>
                      {data.groups.map((g) => {
                        const v = g.totals[m.key]!;
                        return (
                          <td key={g.key}>
                            <MetricTooltip title={m.title.en} value={formatValue(m.format, v.value)} formula={m.formula.en} expression={m.expression} {...(m.window ? { window: m.window.en } : {})} {...(v.explanation ? { explanation: v.explanation.en } : {})} />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              {data.omittedGroups > 0 && <p className="hint">{data.omittedGroups} smaller campaigns are not shown; pick campaigns to compare them.</p>}
              {metric && (
                <>
                  <label>
                    Chart{' '}
                    <select value={metric.key} onChange={(e) => setChartMetric(e.target.value)}>
                      {data.metrics.map((m) => (
                        <option key={m.key} value={m.key}>
                          {m.title.en}
                        </option>
                      ))}
                    </select>
                  </label>
                  <CompareChart title={metric.title.en} format={metric.format} days={data.groups[0]!.series.map((d) => d.day)} series={data.groups.map((g) => ({ label: g.label, values: g.series.map((d) => d.values[metric.key] ?? null) }))} />
                </>
              )}
            </>
          )}
        </>
      )}
    </section>
  );
}
